import { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, TextField, Button, Typography, CircularProgress,
    Alert, MenuItem, Divider
} from "@mui/material";
import BarcodeScanner from "./BarcodeScanner";
import { getInventoryItemByBarcode, createInventoryItem, createInventoryLog } from "./InventoryApi";

const CATEGORIES = ["housekeeping", "maintenance", "front_desk", "food_beverage", "safety"];

const ReceiveStockDialog = ({ open, onClose, onSuccess }) => {

    const [scannedBarcode, setScannedBarcode] = useState("");
    const [item, setItem]           = useState(null);
    const [isNewItem, setIsNewItem] = useState(false);
    const [quantity, setQuantity]   = useState("");
    const [notes, setNotes]         = useState("");
    const [newItem, setNewItem]     = useState({ name: "", category: "", min_stock: "" });
    const [loading, setLoading]     = useState(false);
    const [searching, setSearching] = useState(false);
    const [error, setError]         = useState("");

    const handleReset = () => {
        setScannedBarcode("");
        setItem(null);
        setIsNewItem(false);
        setQuantity("");
        setNotes("");
        setNewItem({ name: "", category: "", min_stock: "" });
        setError("");
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    // Called by BarcodeScanner on Enter or camera scan
    const handleScan = async (barcode) => {
        setScannedBarcode(barcode);
        setSearching(true);
        setError("");
        setItem(null);
        setIsNewItem(false);
        const found = await getInventoryItemByBarcode(barcode);
        setSearching(false);
        if (found) {
            setItem(found);
        } else {
            setIsNewItem(true);
        }
    };

    const handleConfirmReceive = async () => {
        if (!quantity || isNaN(quantity) || Number(quantity) <= 0) {
            setError("Please enter a valid quantity.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            let targetItemId = item?.id;

            if (isNewItem) {
                if (!newItem.name || !newItem.category) {
                    setError("Please fill in item name and category.");
                    setLoading(false);
                    return;
                }
                const created = await createInventoryItem({
                    barcode: scannedBarcode,
                    name: newItem.name,
                    category: newItem.category,
                    min_stock: newItem.min_stock || 0,
                });
                targetItemId = created?.data;
            }

            await createInventoryLog({
                item_id: targetItemId,
                type: "IN",
                quantity: Number(quantity),
                description: notes.trim() || null,
            });

            onSuccess?.();
            handleClose();
        } catch (err) {
            setError("Something went wrong. Please try again.");
        }
        setLoading(false);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>+ Receive Stock</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>

                    {/* Scanner */}
                    <BarcodeScanner
                        onScan={handleScan}
                        disabled={searching || !!item || isNewItem}
                    />

                    {/* Searching indicator */}
                    {searching && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                                Looking up item...
                            </Typography>
                        </Box>
                    )}

                    {/* Existing Item Found */}
                    {item && (
                        <>
                            <Divider />
                            <Box bgcolor="#f5f5f5" borderRadius={1} p={1.5}>
                                <Typography variant="subtitle1" fontWeight={700}>{item.name}</Typography>
                                <Typography variant="body2" color="text.secondary" textTransform="capitalize">
                                    {item.category.replace("_", " ")} · Current Stock: {item.current_stock ?? "—"}
                                </Typography>
                            </Box>
                        </>
                    )}

                    {/* New Item Form */}
                    {isNewItem && (
                        <>
                            <Divider />
                            <Alert severity="info">New item detected. Please fill in the details.</Alert>
                            <TextField
                                label="Item Name"
                                value={newItem.name}
                                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                                size="small"
                                fullWidth
                                autoFocus
                            />
                            <TextField
                                select
                                label="Category"
                                value={newItem.category}
                                onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                                size="small"
                                fullWidth
                            >
                                {CATEGORIES.map((cat) => (
                                    <MenuItem key={cat} value={cat}>
                                        {cat.replace("_", " ").replace(/^\w/, c => c.toUpperCase())}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Minimum Stock Alert"
                                value={newItem.min_stock}
                                onChange={(e) => setNewItem({ ...newItem, min_stock: e.target.value })}
                                size="small"
                                type="number"
                                fullWidth
                            />
                        </>
                    )}

                    {/* Quantity + Notes */}
                    {(item || isNewItem) && (
                        <>
                            <TextField
                                label="Quantity Received"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                size="small"
                                type="number"
                                fullWidth
                                autoFocus
                            />
                            <TextField
                                label="Notes (optional)"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                size="small"
                                fullWidth
                                placeholder="e.g. Weekly delivery from supplier"
                            />
                        </>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                {(item || isNewItem) && (
                    <Button onClick={handleReset} disabled={loading}>
                        Scan Again
                    </Button>
                )}
                {(item || isNewItem) && (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleConfirmReceive}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={18} /> : "Confirm Receive"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ReceiveStockDialog;